import React, { useState, useEffect } from 'react';
import { Layers, AlertCircle, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/common/Combobox';
import { cycleService } from '@/services/cycleService';
import { farmService } from '@/services/farmService';
import {
  OperationType,
  FarmRecord,
  GrowerRecord,
  GrowerContractRecord,
  ProductionUnitRecord,
} from '@/types/database';

interface NewCycleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newCycle: any) => void;
  defaultFarmId?: string | null;
}

export const NewCycleModal: React.FC<NewCycleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultFarmId,
}) => {
  const [operationType, setOperationType] = useState<OperationType>('contract_grower');
  const [cycleCode, setCycleCode] = useState<string>('');
  const [batchCode, setBatchCode] = useState<string>('');
  const [farmId, setFarmId] = useState<string>(defaultFarmId || '');
  const [productionUnitId, setProductionUnitId] = useState<string>('');
  const [growerId, setGrowerId] = useState<string>('');
  const [contractId, setContractId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [expectedSaleDate, setExpectedSaleDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const [farms, setFarms] = useState<FarmRecord[]>([]);
  const [growers, setGrowers] = useState<Array<GrowerRecord & { contracts: GrowerContractRecord[] }>>([]);
  const [productionUnits, setProductionUnits] = useState<ProductionUnitRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Auto-generate starter cycle code when opening
  useEffect(() => {
    if (isOpen) {
      const year = new Date().getFullYear();
      const randomSuffix = Math.floor(100 + Math.random() * 900);
      setCycleCode(`CYCLE-${year}-${randomSuffix}`);
      setBatchCode(`BATCH-${year}-${randomSuffix}`);
      setErrorMsg(null);
    }
  }, [isOpen]);

  // Load master data for dropdowns
  useEffect(() => {
    if (!isOpen) return;

    farmService.getActiveFarms().then((f) => {
      setFarms(f);
      if (!farmId && f.length > 0) {
        setFarmId(defaultFarmId || f[0].id);
      }
    });

    cycleService.getGrowersWithActiveContracts().then((g) => {
      setGrowers(g);
    });
  }, [isOpen, defaultFarmId]);

  // Load production units when farmId or growerId changes
  useEffect(() => {
    if (!isOpen) return;
    cycleService.getProductionUnits(farmId || null, growerId || null).then((units) => {
      setProductionUnits(units);
      if (productionUnitId && !units.find((u) => u.id === productionUnitId)) {
        setProductionUnitId('');
      }
    });
  }, [isOpen, farmId, growerId]);

  // Active contracts for selected grower
  const selectedGrower = growers.find((g) => g.id === growerId);
  const availableContracts = selectedGrower?.contracts || [];

  // Reset contract if grower changes
  useEffect(() => {
    if (availableContracts.length === 1) {
      setContractId(availableContracts[0].id);
    } else if (!availableContracts.some((c) => c.id === contractId)) {
      setContractId('');
    }
  }, [growerId, availableContracts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (operationType === 'contract_grower') {
      if (!growerId) {
        setErrorMsg('Please select a Grower for this Contract Growing cycle.');
        return;
      }
      if (!contractId) {
        setErrorMsg(
          'An active Grower Contract with defined profit sharing percentages is required for Contract Growing.'
        );
        return;
      }
    }

    if (!cycleCode.trim()) {
      setErrorMsg('Cycle code is required.');
      return;
    }
    if (!startDate) {
      setErrorMsg('Placement / Start Date is required.');
      return;
    }

    setLoading(true);
    try {
      const created = await cycleService.createCycle({
        cycle_code: cycleCode.trim(),
        batch_code: batchCode.trim() || cycleCode.trim(),
        grower_id: operationType === 'contract_grower' ? growerId : growerId || null,
        contract_id: operationType === 'contract_grower' ? contractId : null,
        farm_id: farmId || null,
        production_unit_id: productionUnitId || null,
        operation_type: operationType,
        start_date: startDate,
        expected_sale_date: expectedSaleDate || null,
        notes: notes.trim() || null,
      });

      onSuccess(created);
      onClose();
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to create production cycle.');
    } finally {
      setLoading(false);
    }
  };

  const farmOptions = farms.map((f) => ({
    value: f.id,
    label: `${f.farm_name}${f.municipality_city ? ` (${f.municipality_city})` : ''}`,
  }));

  const unitOptions = productionUnits.map((u) => ({
    value: u.id,
    label: `${u.unit_name}${u.unit_code ? ` (${u.unit_code})` : ''}`,
  }));

  const growerOptions = growers.map((g) => ({
    value: g.id,
    label: `${g.full_name} (${g.contracts.length} ${g.contracts.length === 1 ? 'contract' : 'contracts'})`,
  }));

  const contractOptions = availableContracts.map((c) => ({
    value: c.id,
    label: `${c.contract_number || 'Contract'} — Split: ${c.vsb_share_pct}% VSB / ${c.grower_share_pct}% Grower`,
  }));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold">
              <Layers className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-bold text-slate-900">
                Create New Production Cycle
              </DialogTitle>
              <p className="text-xs text-slate-500">
                Initialize a new swine production cycle with bound contract or internal farm relationship.
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {errorMsg && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* 1. Operating Relationship Selection */}
          <div className="space-y-2 bg-slate-50 p-3.5 rounded-lg border border-slate-200">
            <label className="text-xs font-bold text-slate-800 uppercase tracking-wider block">
              1. Operating Relationship *
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label
                className={`flex items-start gap-2.5 p-3 rounded-md border cursor-pointer min-h-[44px] transition-colors ${
                  operationType === 'contract_grower'
                    ? 'border-emerald-600 bg-emerald-50/60 text-slate-900 font-semibold'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="operationType"
                  value="contract_grower"
                  checked={operationType === 'contract_grower'}
                  onChange={() => setOperationType('contract_grower')}
                  className="mt-0.5 text-emerald-700 focus:ring-emerald-600 w-4 h-4"
                />
                <div>
                  <div className="text-xs font-bold">Contract Growing</div>
                  <div className="text-[11px] text-slate-500 font-normal">
                    Requires Grower & explicit Contract profit split
                  </div>
                </div>
              </label>

              <label
                className={`flex items-start gap-2.5 p-3 rounded-md border cursor-pointer min-h-[44px] transition-colors ${
                  operationType === 'company_owned'
                    ? 'border-emerald-600 bg-emerald-50/60 text-slate-900 font-semibold'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="operationType"
                  value="company_owned"
                  checked={operationType === 'company_owned'}
                  onChange={() => setOperationType('company_owned')}
                  className="mt-0.5 text-emerald-700 focus:ring-emerald-600 w-4 h-4"
                />
                <div>
                  <div className="text-xs font-bold">Company-Owned</div>
                  <div className="text-[11px] text-slate-500 font-normal">
                    Internal farm operation (Grower optional)
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* 2. Farm Location & Pen */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Farm Location *</label>
              <Combobox
                options={farmOptions}
                value={farmId}
                onChange={setFarmId}
                placeholder="Select Farm Location..."
                searchPlaceholder="Search farm..."
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Production Unit / Pen <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <Combobox
                options={unitOptions}
                value={productionUnitId}
                onChange={setProductionUnitId}
                placeholder="Select Pen / Unit..."
                searchPlaceholder="Search unit..."
              />
            </div>
          </div>

          {/* 3. Grower & Contract (Enabled for Contract Growing) */}
          {operationType === 'contract_grower' ? (
            <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 space-y-3">
              <label className="text-xs font-bold text-slate-800 uppercase tracking-wider block">
                2. Grower & Bound Contract
              </label>

              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Grower Name *</label>
                <Combobox
                  options={growerOptions}
                  value={growerId}
                  onChange={setGrowerId}
                  placeholder="Select Grower..."
                  searchPlaceholder="Search grower..."
                />
              </div>

              {growerId && (
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-1">
                    Bound Grower Contract *
                  </label>
                  {availableContracts.length === 0 ? (
                    <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-xs">
                      No active contract found for this grower. Contract Growing cycles require an explicit contract with defined profit split percentages.
                    </div>
                  ) : (
                    <Combobox
                      options={contractOptions}
                      value={contractId}
                      onChange={setContractId}
                      placeholder="Select Active Contract..."
                      searchPlaceholder="Search contract..."
                    />
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Assigned Caretaker / Grower <span className="text-slate-400 font-normal">(Optional for Company-Owned)</span>
              </label>
              <Combobox
                options={growerOptions}
                value={growerId}
                onChange={setGrowerId}
                placeholder="None (Direct Farm Operations)..."
                searchPlaceholder="Search grower..."
              />
            </div>
          )}

          {/* 4. Schedule & Identifiers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Cycle Code *</label>
              <Input
                type="text"
                value={cycleCode}
                onChange={(e) => setCycleCode(e.target.value)}
                placeholder="e.g. CYCLE-2026-081"
                className="h-10 text-xs font-mono font-bold"
                required
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Batch Code</label>
              <Input
                type="text"
                value={batchCode}
                onChange={(e) => setBatchCode(e.target.value)}
                placeholder="e.g. BATCH-2026-081"
                className="h-10 text-xs font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Placement / Start Date *</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-10 text-xs font-mono"
                required
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Expected Sale Date <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <Input
                type="date"
                value={expectedSaleDate}
                onChange={(e) => setExpectedSaleDate(e.target.value)}
                className="h-10 text-xs font-mono"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">
              Operational Notes <span className="text-slate-400 font-normal">(Optional)</span>
            </label>
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Target placement head, special feed regime, biosecurity notes..."
              className="h-10 text-xs"
            />
          </div>

          <DialogFooter className="sticky bottom-0 bg-white border-t border-slate-200 pt-3 flex flex-row items-center justify-end gap-2 z-10">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading} className="min-h-[44px]">
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-600 font-semibold gap-1 min-h-[44px]"
              disabled={loading}
            >
              <Plus className="w-4 h-4" /> {loading ? 'Creating...' : 'Create Production Cycle'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
